import React, { useEffect, useState } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Pencil, Coffee, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const blankItem = { name_en: "", name_ar: "", description_en: "", description_ar: "", price: 0, category_id: "", image_url: "", available: true };
const blankCat = { name_en: "", name_ar: "", icon: "coffee", sort_order: 0 };

export default function MenuManagement() {
    const { t, lang } = useApp();
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [itemDlg, setItemDlg] = useState(false);
    const [itemForm, setItemForm] = useState(blankItem);
    const [editingId, setEditingId] = useState(null);
    const [catDlg, setCatDlg] = useState(false);
    const [catForm, setCatForm] = useState(blankCat);
    const [editingCatId, setEditingCatId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error(lang === "ar" ? "يجب أن يكون ملف صورة" : "Must be an image file");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error(lang === "ar" ? "الحجم الأقصى 8 ميجابايت" : "Max size 8 MB");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/menu/upload-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setItemForm((prev) => ({ ...prev, image_url: data.image_url }));
            toast.success(lang === "ar" ? `تم الرفع (${data.size_kb} ك.ب)` : `Uploaded (${data.size_kb} KB)`);
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === "ar" ? "فشل الرفع" : "Upload failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const load = async () => {
        const [c, m] = await Promise.all([api.get("/categories"), api.get("/menu")]);
        setCats(c.data);
        setItems(m.data);
        if (!activeCat && c.data[0]) setActiveCat(c.data[0].id);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const openNewItem = () => {
        setEditingId(null);
        setItemForm({ ...blankItem, category_id: activeCat || (cats[0]?.id || "") });
        setItemDlg(true);
    };

    const openEditItem = (it) => {
        setEditingId(it.id);
        setItemForm({ ...it });
        setItemDlg(true);
    };

    const saveItem = async () => {
        try {
            const body = { ...itemForm, price: Number(itemForm.price) };
            if (!body.name_en || !body.name_ar || !body.category_id) {
                toast.error(t("error")); return;
            }
            if (editingId) {
                await api.patch(`/menu/${editingId}`, body);
                toast.success(t("save"));
            } else {
                await api.post("/menu", body);
                toast.success(t("added"));
            }
            setItemDlg(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm(t("confirm_delete"))) return;
        await api.delete(`/menu/${id}`);
        toast.success(t("removed"));
        await load();
    };

    const openNewCat = () => {
        setEditingCatId(null);
        setCatForm({ ...blankCat, sort_order: cats.length + 1 });
        setCatDlg(true);
    };

    const openEditCat = (c) => {
        setEditingCatId(c.id);
        setCatForm({ name_en: c.name_en, name_ar: c.name_ar, icon: c.icon || "coffee", sort_order: c.sort_order || 0 });
        setCatDlg(true);
    };

    const saveCat = async () => {
        try {
            if (editingCatId) {
                await api.patch(`/categories/${editingCatId}`, catForm);
            } else {
                await api.post("/categories", catForm);
            }
            setCatDlg(false);
            await load();
            toast.success(t("save"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const deleteCat = async (id) => {
        if (!window.confirm(t("confirm_delete"))) return;
        await api.delete(`/categories/${id}`);
        await load();
    };

    const filtered = items.filter((i) => i.category_id === activeCat);

    return (
        <div className="space-y-4" data-testid="menu-mgmt-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("manage_menu")}</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={openNewCat} data-testid="add-category-btn"><Plus className="h-4 w-4 me-2" />{t("add_category")}</Button>
                    <Button onClick={openNewItem} data-testid="add-item-btn"><Plus className="h-4 w-4 me-2" />{t("add_item")}</Button>
                </div>
            </div>

            <Tabs value={activeCat || ""} onValueChange={setActiveCat}>
                <TabsList className="flex flex-wrap gap-1 h-auto bg-transparent">
                    {cats.map((c) => (
                        <TabsTrigger key={c.id} value={c.id} data-testid={`cat-tab-${c.id}`} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                            {lang === "ar" ? c.name_ar : c.name_en}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {cats.map((c) => (
                    <TabsContent key={c.id} value={c.id} className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm text-muted-foreground">{lang === "ar" ? c.name_ar : c.name_en}</div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditCat(c)} data-testid={`edit-cat-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => deleteCat(c.id)} data-testid={`delete-cat-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filtered.length === 0 ? (
                                <div className="col-span-full text-center py-10 text-muted-foreground">{t("no_menu_items")}</div>
                            ) : filtered.map((it) => (
                                <Card key={it.id} className="grain bg-card/70 overflow-hidden" data-testid={`menu-row-${it.id}`}>
                                    <div className="aspect-[4/3] bg-secondary relative">
                                        {it.image_url ? (
                                            <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center"><Coffee className="h-10 w-10 text-primary/30" /></div>
                                        )}
                                        {!it.available && <div className="absolute inset-0 bg-background/70 flex items-center justify-center text-xs uppercase tracking-wider text-muted-foreground">{t("unavailable")}</div>}
                                    </div>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <div>
                                                <div className="font-medium">{lang === "ar" ? it.name_ar : it.name_en}</div>
                                                <div className="text-xs text-muted-foreground">{lang === "ar" ? it.name_en : it.name_ar}</div>
                                            </div>
                                            <div className="text-primary font-medium tabular-nums whitespace-nowrap">{fmtSDG(it.price, lang)}</div>
                                        </div>
                                        <div className="flex gap-1 mt-3">
                                            <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditItem(it)} data-testid={`edit-item-${it.id}`}><Pencil className="h-3 w-3 me-1" />{t("edit")}</Button>
                                            <Button variant="ghost" size="sm" onClick={() => deleteItem(it.id)} data-testid={`delete-item-${it.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                ))}
            </Tabs>

            {/* Item dialog */}
            <Dialog open={itemDlg} onOpenChange={setItemDlg}>
                <DialogContent data-testid="item-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingId ? t("edit") : t("add_item")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>{t("name_en")}</Label><Input value={itemForm.name_en} onChange={(e) => setItemForm({ ...itemForm, name_en: e.target.value })} data-testid="item-name-en" /></div>
                            <div className="space-y-1.5"><Label>{t("name_ar")}</Label><Input value={itemForm.name_ar} onChange={(e) => setItemForm({ ...itemForm, name_ar: e.target.value })} dir="rtl" data-testid="item-name-ar" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>{t("desc_en")}</Label><Textarea rows={2} value={itemForm.description_en} onChange={(e) => setItemForm({ ...itemForm, description_en: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>{t("desc_ar")}</Label><Textarea rows={2} dir="rtl" value={itemForm.description_ar} onChange={(e) => setItemForm({ ...itemForm, description_ar: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t("category")}</Label>
                                <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}>
                                    <SelectTrigger data-testid="item-cat-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {cats.map((c) => <SelectItem key={c.id} value={c.id}>{lang === "ar" ? c.name_ar : c.name_en}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5"><Label>{t("price")} (SDG)</Label><Input type="number" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} data-testid="item-price" /></div>
                        </div>
                        <div className="space-y-2">
                            <Label>{t("image_url")}</Label>
                            {/* Preview */}
                            {itemForm.image_url && (
                                <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-border bg-secondary/40">
                                    <img src={itemForm.image_url} alt="" className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => setItemForm({ ...itemForm, image_url: "" })}
                                        className="absolute top-1 end-1 bg-black/70 hover:bg-black text-white rounded-full p-1"
                                        data-testid="remove-image-btn"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            )}
                            {/* Upload + URL row */}
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    data-testid="item-image-file"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="gap-2 sm:w-44 justify-center"
                                    data-testid="upload-image-btn"
                                >
                                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {uploading ? (lang === "ar" ? "جاري الرفع..." : "Uploading...") : (lang === "ar" ? "رفع صورة" : "Upload Image")}
                                </Button>
                                <Input
                                    value={itemForm.image_url.startsWith("data:") ? "" : itemForm.image_url}
                                    onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                                    placeholder={lang === "ar" ? "أو ألصق رابط صورة (https://...)" : "Or paste image URL"}
                                    data-testid="item-image"
                                    className="flex-1"
                                />
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                                {lang === "ar"
                                    ? "JPG / PNG / WEBP حتى 8 ميجابايت — يتم ضغطها تلقائياً"
                                    : "JPG / PNG / WEBP up to 8 MB — auto-compressed"}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                            <Switch checked={itemForm.available} onCheckedChange={(v) => setItemForm({ ...itemForm, available: v })} data-testid="item-available" />
                            <Label>{t("available")}</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setItemDlg(false)}>{t("cancel")}</Button>
                        <Button onClick={saveItem} data-testid="save-item-btn">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Category dialog */}
            <Dialog open={catDlg} onOpenChange={setCatDlg}>
                <DialogContent data-testid="cat-dialog">
                    <DialogHeader><DialogTitle>{editingCatId ? t("edit") : t("add_category")}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>{t("name_en")}</Label><Input value={catForm.name_en} onChange={(e) => setCatForm({ ...catForm, name_en: e.target.value })} data-testid="cat-name-en" /></div>
                        <div className="space-y-1.5"><Label>{t("name_ar")}</Label><Input dir="rtl" value={catForm.name_ar} onChange={(e) => setCatForm({ ...catForm, name_ar: e.target.value })} data-testid="cat-name-ar" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Icon</Label><Input value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Sort</Label><Input type="number" value={catForm.sort_order} onChange={(e) => setCatForm({ ...catForm, sort_order: Number(e.target.value) })} /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCatDlg(false)}>{t("cancel")}</Button>
                        <Button onClick={saveCat} data-testid="save-cat-btn">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
